import type { SourceMapInput } from '@jridgewell/trace-mapping';
import React from 'react';

import { AiComponentCompileError } from './AiComponentCompileError';
import {
  hashPageSource,
  pageSourceUrl,
  registerPageSourceDiagnostics,
  resolveOriginalLocation
} from './pageSourceDiagnostics';
import { createRuntimeReact, publishedReact } from './runtimeReact';

/**
 * The page runtime calls the compiled component rather than rendering it as an
 * element of its own, so what a source returns has to be a function component —
 * a class cannot be called. `compileAiComponent` rejects anything else, which is
 * what makes this type honest.
 */
export type CompiledAiComponent =
  | { Component: React.FunctionComponent<Record<string, unknown>>; error: null }
  | { Component: null; error: AiComponentCompileError | null };

export type PageRuntimeSourceKind = 'page' | 'layout' | 'shared';

type CompileMetric = {
  kind: PageRuntimeSourceKind;
  sourceBytes: number;
  durationMs: number;
};

declare global {
  interface Window {
    __diagonalPageRuntimeCompileMetrics?: CompileMetric[];
  }
}

function recordCompileMetric(metric: CompileMetric): void {
  if (typeof window !== 'undefined') {
    (window.__diagonalPageRuntimeCompileMetrics ??= []).push(metric);
  }
}

export type CompileAiComponentOptions = {
  authoringFilePath?: string;
  sourcePath?: string;
  kind?: PageRuntimeSourceKind;
};

/**
 * Transformed output ready to evaluate, whichever thread produced it. The
 * diagnostic fields travel together: without them a stack frame in this source
 * cannot be attributed to an authored file, and without the map it cannot be
 * mapped back to an authored line.
 */
export type TransformedPageSourceWithDiagnostics = {
  code: string;
  sourcePath: string;
  sourceLabel: string;
  /** Null for publish-time transformed source, which ships without a map. */
  sourceMap: SourceMapInput | null;
  codeHash: string;
};

export type TransformedPageSource =
  | TransformedPageSourceWithDiagnostics
  | {
      code: string;
      sourcePath?: never;
      sourceLabel?: never;
      sourceMap?: never;
      codeHash?: never;
    };

export type EvaluateTransformedComponentOptions = {
  authoring: boolean;
  kind: PageRuntimeSourceKind;
  sourceBytes?: number;
  /** When the work this metric measures began. */
  startedAt?: number;
};

// V8 adds two wrapper lines before a new Function body.
const NEW_FUNCTION_LINE_OFFSET = 2;

function toCompileError(error: unknown, sourcePath?: string): AiComponentCompileError {
  if (!(error instanceof Error)) {
    return new AiComponentCompileError(String(error), { sourcePath });
  }

  const location = resolveOriginalLocation(error);
  const compileError = new AiComponentCompileError(error.message, {
    cause: error,
    sourcePath,
    ...(location
      ? {
          sourceLabel: pageSourceUrl(location.sourcePath),
          line: location.line,
          column: location.column
        }
      : sourcePath
        ? { sourceLabel: pageSourceUrl(sourcePath) }
        : {})
  });
  compileError.stack = error.stack;

  return compileError;
}

const authoringReact = createRuntimeReact(true);

/** A class component is constructed, never called, so the runtime cannot render one. */
function isClassComponent(candidate: unknown): boolean {
  return (
    (candidate as { prototype?: { isReactComponent?: unknown } }).prototype?.isReactComponent !==
    undefined
  );
}

/**
 * Evaluates already-transformed source against an `Sdk`.
 *
 * This stays on the document's main thread even when the transform runs in a
 * worker: a page's top-level code closes over `Sdk`, so the factory has to be
 * called again whenever `Sdk` changes, against the very `React` the document
 * renders with.
 *
 * SECURITY: this evaluates author-provided code in the host page context via
 * `new Function`. It is gated behind page-builder authoring (no end-user input
 * path writes this prop). This single function is the seam to swap for a
 * sandboxed iframe / restricted interpreter later without changing the block.
 */
export function evaluateTransformedComponent(
  transformed: TransformedPageSource,
  Sdk: unknown,
  { authoring, kind, sourceBytes = 0, startedAt }: EvaluateTransformedComponentOptions
): CompiledAiComponent {
  const measuredFrom = startedAt ?? performance.now();
  const metric = (): CompileMetric => ({
    kind,
    sourceBytes,
    durationMs: performance.now() - measuredFrom
  });
  let code = transformed.code;

  if (transformed.sourceLabel) {
    if (transformed.sourcePath && transformed.codeHash) {
      registerPageSourceDiagnostics(transformed.sourceLabel, {
        sourcePath: transformed.sourcePath,
        codeHash: transformed.codeHash,
        sourceMap: transformed.sourceMap,
        lineOffset: NEW_FUNCTION_LINE_OFFSET
      });
    }

    code = `${code}\n//# sourceURL=${transformed.sourceLabel}`;
  }

  try {
    // eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval
    const factory = new Function('React', 'Sdk', code) as (
      react: typeof React,
      Sdk: unknown
    ) => unknown;

    const result = factory(authoring ? authoringReact : publishedReact, Sdk);

    recordCompileMetric(metric());

    if (typeof result !== 'function' || isClassComponent(result)) {
      return {
        Component: null,
        error: new AiComponentCompileError('The code must `return` a React component function.', {
          sourcePath: transformed.sourcePath,
          ...(transformed.sourceLabel ? { sourceLabel: transformed.sourceLabel } : {})
        })
      };
    }

    return { Component: result as React.FunctionComponent<Record<string, unknown>>, error: null };
  } catch (error) {
    recordCompileMetric(metric());

    return { Component: null, error: toCompileError(error, transformed.sourcePath) };
  }
}

/**
 * Evaluates a published source into a renderable component on the calling
 * thread.
 *
 * The source is the transformed output publication stored, treated as the body
 * of a factory `(React, Sdk) => Component`, so it must `return` a React
 * component function. Nothing on this path transforms JSX: a published render
 * evaluates what publication produced, and the authoring preview transforms in
 * its worker and calls `evaluateTransformedComponent` directly.
 */
export function compileAiComponent(
  code: string | null | undefined,
  Sdk: unknown = {},
  options: CompileAiComponentOptions = {}
): CompiledAiComponent {
  if (!code || !code.trim()) {
    // No code yet — render the empty state, not an error.
    return { Component: null, error: null };
  }

  const kind = options.kind ?? 'page';
  const sourceBytes = new Blob([code]).size;
  const startedAt = performance.now();
  const transformed: TransformedPageSource = options.sourcePath
    ? {
        code,
        sourcePath: options.sourcePath,
        sourceLabel: pageSourceUrl(options.sourcePath),
        sourceMap: null,
        codeHash: hashPageSource(code)
      }
    : { code };

  return evaluateTransformedComponent(transformed, Sdk, {
    authoring: Boolean(options.authoringFilePath),
    kind,
    sourceBytes,
    startedAt
  });
}
